import axios from 'axios';
import { auth } from '../../../firebase';
import { cleanJsonEmptyValues } from '../../../utils/formatUtils';
import {
  DashboardPolicySummary,
  Policy,
  PolicyCreateArgs,
  PolicyForm,
  PolicySummary,
} from '../../Interfaces/Insurance/Policy';
import { Status } from '../../Interfaces/system/Generic.interfaces';
import { Company } from '../../Interfaces/admin/User.interfaces';

const apiUrl = process.env['NX_API_URL'];

export const fetchPolicies = async (
  companyID: string,
  status: Status,
  signal?: AbortSignal
): Promise<PolicySummary[]> => {
  const response = await axios.get(`${apiUrl}/api/policy`, {
    signal,
    params: { companyID, status },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });
  const dashboard = response.data;
  return dashboard;
};

export const fetchActivePoliciesSummary = async (
  companyID: string,
  signal?: AbortSignal
): Promise<DashboardPolicySummary[]> => {
  const response = await axios.get(`${apiUrl}/api/policy/summary`, {
    signal,
    params: { companyID },
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });
  const dashboard = response.data;
  return dashboard;
};

export const fetchPolicy = async (
  id: string,
  signal?: AbortSignal
): Promise<Policy> => {
  const response = await axios.get(`${apiUrl}/api/policy/${id}`, {
    signal,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });
  const dashboard = response.data;
  return dashboard;
};

export const fetchPolicyAttachment = async (
  id: string,
  signal?: AbortSignal
) => {
  const response = await axios.get(`${apiUrl}/api/policy/${id}/attachment`, {
    signal,
    responseType: 'blob',
    headers: {
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });

  return response;
};

export const postPolicy = async (
  company: Company,
  body: PolicyCreateArgs
): Promise<Policy> => {
  const { attachment, ...otherArgs } = body;
  const cleanBody = cleanJsonEmptyValues(otherArgs);

  const response = await axios.post(
    `${apiUrl}/api/policy`,
    { ...cleanBody, companyID: company.id, attachment },
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );

  return response.data;
};

export const putPolicy = async (
  id: string,
  body: PolicyForm
): Promise<Policy> => {
  const { attachment, ...otherArgs } = body;
  const cleanBody = cleanJsonEmptyValues(otherArgs);

  const response = await axios.put(
    `${apiUrl}/api/policy/${id}`,
    { ...cleanBody, attachment },
    {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
    }
  );

  return response.data;
};

export const removePolicy = async (id: string) => {
  await axios.delete(`${apiUrl}/api/policy/${id}`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
    },
  });
};

export const postConsolidatedReport = async (
  companies: string[],
  periodStart?: Date,
  periodEnd?: Date,
  status?: boolean
) => {
  const response = await axios.post(
    `${apiUrl}/api/policy/consolidatedReport`,
    { companies, periodStart, periodEnd, status },
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
      },
      responseType: 'blob',
    }
  );

  return response;
};
